import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Moon, ChevronRight } from 'lucide-react';
import { usePlayerData } from './PlayerDataContext';
import { getSsgPeriods, getSsgPlayerSummary } from '../../api';
import { isPushSupported, isIos, isStandalone, enablePushNotifications } from '../../pushNotifications';
import { C, FONT_UI, FONT_MONO, ZONE_COLOR, ZONE_LEGEND, rpeColor } from './dayspineTokens';
import useDaySpineData from './useDaySpineData';
import PendingFormModal from './PendingFormModal';
import PlayerPillarStanding from './PlayerPillarStanding';

// "Day spine v2" (design handoff option 7a) — an assistant-toned redesign, a full departure
// from the two prior "dashboard" redesigns (holo dial / arc console) rather than a restyle of
// either. See useDaySpineData.js's header comment for the two things the mockup shows that
// HeraLabX's data model genuinely can't produce (exact session clock times, backfilling a
// missed prior day) and how they were honestly adapted instead of faked.

const PUSH_PROMPT_DISMISSED_KEY = 'push_prompt_dismissed';

function NotificationsPrompt({ userId }) {
  const [dismissed, setDismissed]   = useState(() => localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY) === 'true');
  const [requesting, setRequesting] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  const supported = isPushSupported();
  const permission = supported && typeof Notification !== 'undefined' ? Notification.permission : 'denied';

  if (!supported || dismissed || permission !== 'default') return null;

  const dismiss = () => { localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, 'true'); setDismissed(true); };

  const handleEnable = async () => {
    if (isIos() && !isStandalone()) { setShowIosHelp(true); return; }
    setRequesting(true);
    const res = await enablePushNotifications(userId);
    setRequesting(false);
    if (res.ok || res.reason === 'denied') dismiss();
  };

  return (
    <div style={{ borderRadius: 20, background: C.surface, border: `1px solid ${C.hairline08}`, padding: '15px 17px', boxShadow: '0 2px 16px rgba(27,37,64,.05)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Bell size={17} color={C.blue} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ flex: 1 }}>
          {!showIosHelp ? (
            <>
              <div style={{ font: '700 13px ' + FONT_UI, color: C.ink, marginBottom: 4 }}>Stay in the loop</div>
              <div style={{ font: '400 12px/1.5 ' + FONT_UI, color: C.ink60, marginBottom: 10 }}>
                Get notified when new training data, medical updates, or reminders come in.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleEnable} disabled={requesting}
                  style={{ padding: '9px 14px', borderRadius: 11, border: 'none', background: C.blue, color: '#fff', font: '700 12.5px ' + FONT_UI, cursor: requesting ? 'default' : 'pointer' }}>
                  {requesting ? 'Enabling…' : 'Enable notifications'}
                </button>
                <button onClick={dismiss}
                  style={{ padding: '9px 14px', borderRadius: 11, border: `1px solid ${C.hairline18}`, background: 'transparent', color: C.ink60, font: '600 12.5px ' + FONT_UI, cursor: 'pointer' }}>
                  Not now
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ font: '700 13px ' + FONT_UI, color: C.ink, marginBottom: 4 }}>Install the app first</div>
              <div style={{ font: '400 12px/1.5 ' + FONT_UI, color: C.ink60, marginBottom: 10 }}>
                iOS only allows notifications for installed apps. Tap the Share icon in Safari, then "Add to Home Screen" — then open HeraLabX from your home screen and try again.
              </div>
              <button onClick={dismiss}
                style={{ padding: '9px 14px', borderRadius: 11, border: `1px solid ${C.hairline18}`, background: 'transparent', color: C.ink60, font: '600 12.5px ' + FONT_UI, cursor: 'pointer' }}>
                Got it
              </button>
            </>
          )}
        </div>
        <button onClick={dismiss} style={{ background: 'none', border: 'none', color: C.ink40, cursor: 'pointer', padding: 2, flexShrink: 0 }}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function Sk({ w = '100%', h = 14, br = 8, mb = 0 }) {
  return <div style={{ width: w, height: h, borderRadius: br, marginBottom: mb, background: C.hairline08, animation: 'skpulse 1.4s ease-in-out infinite' }} />;
}

const TASK_PALETTE = {
  wellness:  { pending: { accent: C.blue, bg: C.wellnessCard, bd: C.blueBorderStrong }, done: { accent: C.teal, bg: C.tealTint, bd: C.tealBorder } },
  rpe:       { pending: { accent: C.orange, bg: C.orangeCard, bd: C.orangeCardBorder }, done: { accent: C.teal, bg: C.tealTint, bd: C.tealBorder } },
  form:      { pending: { accent: C.blue, bg: C.wellnessCard, bd: C.blueBorderStrong }, done: { accent: C.teal, bg: C.tealTint, bd: C.tealBorder } },
};

export default function PlayerToday({ user }) {
  const navigate = useNavigate();
  const { playerRecord, pendingForms, lateMatchRpe } = usePlayerData();
  const data = useDaySpineData();
  const [ssgSummary, setSsgSummary] = useState(null);
  const [pendingModalDismissed, setPendingModalDismissed] = useState(false);

  useEffect(() => {
    if (!playerRecord?.id) return;
    const pid = playerRecord.id;
    getSsgPeriods().then(periods => {
      const active = periods.find(p => p.status === 'active') || periods[0];
      if (!active) return null;
      return getSsgPlayerSummary(pid, active.id);
    }).then(sum => setSsgSummary(sum || null)).catch(() => {});
  }, [playerRecord?.id]);

  if (data.loading) {
    return (
      <div style={{ minHeight: '100%', background: C.page, padding: '22px 22px 0' }}>
        <style>{`@keyframes skpulse{0%,100%{opacity:1}50%{opacity:.35}}`}</style>
        <Sk h={32} br={8} mb={14} />
        <Sk h={140} br={26} mb={14} />
        <Sk h={200} br={23} mb={14} />
      </div>
    );
  }

  const tasks = [
    ...data.tasks,
    ...pendingForms.map(f => ({
      key: `form-${f.form_id}`, kind: 'form', path: `/player/forms/${f.form_id}`, submitted: false,
      when: f.overdue ? 'OVERDUE' : 'FORM',
      title: f.title,
      sub: f.overdue ? 'Please fill this in as soon as you can.' : 'Waiting for your response.',
      cta: 'Fill now',
    })),
  ];

  // Late-game RPE button sits under the Pitch RPE card — or under Wellness when MD+1 has no pitch
  // session planned (the usual case), so it still lands near the top of the spine.
  const lateRpeAnchor = lateMatchRpe ? (tasks.some(t => t.key === 'pitch-rpe') ? 'pitch-rpe' : 'wellness') : null;

  const leagueOrdinal = ssgSummary ? (ssgSummary.rank === 1 ? 'st' : ssgSummary.rank === 2 ? 'nd' : ssgSummary.rank === 3 ? 'rd' : 'th') : '';
  const leagueTitle = ssgSummary
    ? `${ssgSummary.shared_rank ? 'Joint ' : ''}${ssgSummary.rank}${leagueOrdinal} in the league`
    : 'League standing unavailable';

  return (
    <div style={{ minHeight: '100%', background: C.page, color: C.ink, fontFamily: FONT_UI, paddingBottom: 26 }}>
      <style>{`
        @keyframes skpulse{0%,100%{opacity:1}50%{opacity:.35}}
        @keyframes dsBreathe{0%,100%{transform:scale(1);opacity:.5}50%{transform:scale(1.06);opacity:.85}}
        @keyframes dsPulsedot{0%,100%{opacity:.35;transform:scale(1)}50%{opacity:1;transform:scale(1.35)}}
        @media (prefers-reduced-motion: reduce){ .ds-breathe,.ds-pulsedot{animation:none !important} }
      `}</style>

      {!pendingModalDismissed && pendingForms.length > 0 && (
        <PendingFormModal forms={pendingForms} onDismiss={() => setPendingModalDismissed(true)} />
      )}

      {/* ── 1. Greeting header ── */}
      <div style={{ padding: '22px 22px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: '500 11px ' + FONT_MONO, letterSpacing: '.14em', color: C.ink45 }}>{data.eyebrow}</div>
            <div style={{ font: '700 27px/1.15 ' + FONT_UI, letterSpacing: '-.8px', marginTop: 9 }}>
              Good {data.timeOfDay},<br />{data.firstName}
            </div>
          </div>
          <div style={{ position: 'relative', width: 62, height: 62, flexShrink: 0 }}>
            <div className="ds-breathe" style={{ position: 'absolute', inset: -5, borderRadius: '50%', background: C.blueTint, animation: 'dsBreathe 5s ease-in-out infinite' }} />
            <div style={{
              position: 'relative', width: 62, height: 62, borderRadius: '50%', overflow: 'hidden',
              background: playerRecord?.photo_url ? 'transparent' : 'repeating-linear-gradient(135deg,#e3e7f3 0 6px,#dae0ef 6px 12px)',
              border: '2px solid #fff', boxShadow: '0 4px 14px rgba(27,37,64,.12)',
            }}>
              {playerRecord?.photo_url && (
                <img src={playerRecord.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── 2. Hera day brief ── */}
      <div style={{ padding: '16px 22px 0' }}>
        <div style={{ borderRadius: 26, background: 'linear-gradient(150deg,#eef2ff,#fff 62%)', border: `1px solid ${C.blueBorder}`, padding: '17px 19px 16px', boxShadow: '0 3px 18px rgba(27,37,64,.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="ds-pulsedot" style={{ width: 7, height: 7, borderRadius: '50%', background: C.blue, animation: 'dsPulsedot 2.4s ease-in-out infinite' }} />
            <div style={{ font: '600 10px ' + FONT_MONO, letterSpacing: '.16em', color: C.blue }}>YOUR DAY · HERA</div>
          </div>
          <div style={{ font: '400 13.5px/1.62 ' + FONT_UI, color: C.ink70, marginTop: 11 }}>{data.brief}</div>
          {data.briefTags.length > 0 && (
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 14 }}>
              {data.briefTags.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 11px', borderRadius: 11, background: b.bg, color: b.fg, font: '600 11px ' + FONT_UI, whiteSpace: 'nowrap' }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: b.dot }} />{b.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── 3. Task spine ── */}
      <div style={{ padding: '18px 22px 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {tasks.map(task => {
            const pal = TASK_PALETTE[task.kind] || TASK_PALETTE.form;
            const state = task.submitted ? pal.done : pal.pending;
            return (
              <React.Fragment key={task.key}>
              <div style={{ display: 'flex', gap: 14 }}>
                <div style={{ width: 30, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: state.accent, boxShadow: `0 0 0 4px ${state.bg}`, marginTop: 21 }} />
                  <div style={{ flex: 1, width: 2, background: C.hairline10 }} />
                </div>
                <div onClick={() => navigate(task.path)} style={{
                  flex: 1, minWidth: 0, cursor: 'pointer', marginBottom: 11, padding: '16px 17px 17px', borderRadius: 23,
                  background: state.bg, border: `1px solid ${state.bd}`, transition: 'all .28s ease',
                }}>
                  <div style={{ font: '500 10.5px ' + FONT_MONO, letterSpacing: '.12em', color: state.accent }}>{task.when}</div>
                  <div style={{ font: '700 16.5px/1.3 ' + FONT_UI, letterSpacing: '-.3px', marginTop: 7 }}>{task.title}</div>
                  <div style={{ font: '400 12.5px/1.5 ' + FONT_UI, color: C.ink60, marginTop: 4 }}>{task.sub}</div>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 13, padding: '13px 16px', borderRadius: 15,
                    font: '700 13.5px ' + FONT_UI,
                    background: task.submitted ? C.tealBtn : state.accent,
                    color: task.submitted ? C.teal : '#fff',
                  }}>
                    {task.cta}
                  </div>
                </div>
              </div>
              {task.key === lateRpeAnchor && (
                <div style={{ display: 'flex', gap: 14 }}>
                  <div style={{ width: 30, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    <div style={{ width: 2, background: C.hairline10 }} />
                  </div>
                  <button onClick={() => navigate(`/player/forms/rpe?date=${lateMatchRpe.date}`)} style={{
                    flex: 1, minWidth: 0, marginBottom: 11, padding: '10px 14px', borderRadius: 14, cursor: 'pointer',
                    background: 'transparent', border: `1px dashed ${C.orange}`, color: C.orangeInk,
                    font: '700 12.5px ' + FONT_UI, textAlign: 'center',
                  }}>
                    Fill in game RPE{lateMatchRpe.opponent ? ` · vs ${lateMatchRpe.opponent}` : ''}
                  </button>
                </div>
              )}
              </React.Fragment>
            );
          })}
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={{ width: 30, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', border: `2px solid ${C.hairline18}`, marginTop: 19 }} />
            </div>
            <div style={{ flex: 1, padding: '17px 0 2px', font: '400 12.5px ' + FONT_UI, color: C.ink50 }}>
              {data.briefTags.length > 0
                ? `${data.briefTags.map(t => t.label).join(' · ')} — RPE opens after each`
                : 'No more sessions scheduled today'}
            </div>
          </div>
        </div>
      </div>

      {/* ── 3b. Where you stand (pillar standing add-on) ── */}
      <div style={{ padding: '16px 22px 0' }}>
        <PlayerPillarStanding playerRecord={playerRecord} />
      </div>

      <div style={{ padding: '12px 22px 0' }}>
        <NotificationsPrompt userId={user?.id} />
      </div>

      {/* ── 4. Last 7 days ── */}
      <div style={{ padding: '12px 22px 0' }}>
        <div style={{ borderRadius: 28, background: C.surface, padding: '20px 18px 16px', boxShadow: '0 2px 16px rgba(27,37,64,.05)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 2px' }}>
            <div style={{ font: '700 15px ' + FONT_UI, letterSpacing: '-.2px' }}>Last 7 days</div>
            <div style={{ font: '400 11px ' + FONT_UI, color: C.ink45 }}>{data.dateRangeLabel}</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', marginTop: 16 }}>
            {data.days.map(d => (
              <div key={d.date} style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 38, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 13, flexShrink: 0,
                    background: d.isToday ? C.blue : C.page,
                    border: `1px solid ${d.isToday ? C.blue : C.hairline08}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <div style={{ font: '600 8.5px ' + FONT_MONO, letterSpacing: '.08em', color: d.isToday ? 'rgba(255,255,255,.7)' : C.ink45 }}>{d.dow}</div>
                    <div style={{ font: '700 13px ' + FONT_UI, color: d.isToday ? '#fff' : C.ink, lineHeight: 1.1 }}>{d.dom}</div>
                  </div>
                  <div style={{ flex: 1, width: 2, background: C.hairline08, margin: '5px 0' }} />
                </div>

                <div style={{ flex: 1, minWidth: 0, paddingBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 38 }}>
                    <div style={{ font: '700 13px ' + FONT_UI, letterSpacing: '-.2px' }}>{d.title}</div>
                    <div style={{
                      padding: '3px 8px', borderRadius: 8, font: '600 9px ' + FONT_MONO, letterSpacing: '.1em', whiteSpace: 'nowrap',
                      background: d.tagKind === 'match' ? C.redTint : d.tagKind === 'today' ? C.blueTint : C.hairline06,
                      color: d.tagKind === 'match' ? C.redInk : d.tagKind === 'today' ? C.blue : C.ink50,
                    }}>
                      {d.tag}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, font: '500 10px ' + FONT_MONO, letterSpacing: '.06em', color: C.ink42, whiteSpace: 'nowrap' }}>
                      <Moon size={9} color={C.ink42} strokeWidth={2} />SLEEP
                    </div>
                    <div style={{ flex: 1, height: 7, borderRadius: 5, background: C.hairline06, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 5, transition: 'width .6s cubic-bezier(.2,.8,.2,1)',
                        width: `${d.sleepMin != null ? Math.min((d.sleepMin / 540) * 100, 100) : 0}%`,
                        background: d.sleepMin != null && d.sleepMin < 390 ? 'linear-gradient(90deg,#f5a300,#f7bb52)' : 'linear-gradient(90deg,#3f6cf5,#6a5bd6)',
                      }} />
                    </div>
                    <div style={{ font: '700 12px ' + FONT_UI, color: d.sleepMin != null && d.sleepMin < 390 ? C.orangeInk : C.ink, whiteSpace: 'nowrap' }}>{fmtSleepLocal(d.sleepMin)}</div>
                  </div>

                  {d.items.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 10 }}>
                      {d.items.map(it => (
                        <div key={it.key}
                          onClick={it.sessionId ? () => navigate(`/player/me/gps/team-training?session=${it.sessionId}`) : undefined}
                          style={{ padding: '9px 11px 10px', borderRadius: 14, background: C.page, border: `1px solid ${C.hairline06}`, cursor: it.sessionId ? 'pointer' : 'default' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: it.type === 'pitch' ? C.blue : it.type === 'gym' ? C.plum : C.redOrange }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ font: '600 12px ' + FONT_UI, color: 'rgba(27,37,64,.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</div>
                              <div style={{ font: '400 10.5px ' + FONT_UI, color: C.ink50, marginTop: 1 }}>{it.meta}</div>
                            </div>
                            <div style={{
                              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 38,
                              padding: '5px 8px', borderRadius: 11, flexShrink: 0,
                              background: it.rpeFilled ? `color-mix(in oklab, ${rpeColor(it.rpe)} 12%, #fff)` : 'rgba(245,163,0,.1)',
                              border: it.rpeFilled ? '1px solid transparent' : '1px dashed rgba(245,163,0,.55)',
                              color: it.rpeFilled ? rpeColor(it.rpe) : C.orange,
                            }}>
                              <div style={{ font: '700 14px/1 ' + FONT_UI }}>{it.rpeFilled ? it.rpe : '—'}</div>
                              <div style={{ font: '600 7.5px ' + FONT_MONO, letterSpacing: '.08em', marginTop: 2 }}>{it.rpeFilled ? 'RPE' : 'LOG'}</div>
                            </div>
                          </div>
                          {it.metrics.length > 0 && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
                              {it.metrics.map(mx => (
                                <div key={mx.key} style={{ flex: 1, borderRadius: 10, background: `color-mix(in oklab, ${ZONE_COLOR[mx.zone] || C.ink40} 10%, #fff)`, padding: '7px 8px 8px', textAlign: 'center' }}>
                                  <div style={{ font: '600 8px ' + FONT_MONO, letterSpacing: '.1em', color: C.ink50 }}>{mx.label}</div>
                                  <div style={{ font: '700 14px/1 ' + FONT_UI, color: ZONE_COLOR[mx.zone] || C.ink, marginTop: 4 }}>{mx.val != null ? Math.round(mx.val) : '—'}</div>
                                  <div style={{ font: '400 8.5px ' + FONT_UI, color: C.ink40, marginTop: 2 }}>{mx.unit}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 12px', padding: '0 2px 14px', borderBottom: `1px solid ${C.hairline08}` }}>
            {ZONE_LEGEND.map(lg => (
              <div key={lg.status} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 7, height: 7, borderRadius: 3, background: ZONE_COLOR[lg.status] }} />
                <div style={{ font: '500 9.5px ' + FONT_UI, color: C.ink50, whiteSpace: 'nowrap' }}>{lg.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 9, marginTop: 2, paddingTop: 14 }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ font: '700 18px ' + FONT_UI }}>{fmtSleepLocal(data.avgSleep)}</div>
              <div style={{ font: '400 10.5px ' + FONT_UI, color: C.ink50, marginTop: 2 }}>avg sleep</div>
            </div>
            <div style={{ width: 1, background: C.hairline08 }} />
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ font: '700 18px ' + FONT_UI }}>{data.avgRpe != null ? data.avgRpe : '—'}</div>
              <div style={{ font: '400 10.5px ' + FONT_UI, color: C.ink50, marginTop: 2 }}>avg RPE</div>
            </div>
            <div style={{ width: 1, background: C.hairline08 }} />
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ font: '700 18px ' + FONT_UI }}>{data.sessionCount}</div>
              <div style={{ font: '400 10.5px ' + FONT_UI, color: C.ink50, marginTop: 2 }}>sessions</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 5. This week's load ── */}
      <div style={{ padding: '12px 22px 0' }}>
        <div style={{ borderRadius: 28, background: C.surface, padding: '20px 20px 18px', boxShadow: '0 2px 16px rgba(27,37,64,.05)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <div style={{ font: '700 15px ' + FONT_UI, letterSpacing: '-.2px' }}>This week's load</div>
            <div style={{ font: '700 14px ' + FONT_UI, color: C.blue }}>{data.weekPct}%</div>
          </div>
          <div style={{ height: 12, borderRadius: 8, background: 'rgba(27,37,64,.07)', marginTop: 14, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 8, width: `${Math.min(data.weekPct, 100)}%`, background: 'linear-gradient(90deg,#3f6cf5,#6a5bd6)', transition: 'width .6s ease' }} />
          </div>
          <div style={{ font: '400 12px ' + FONT_UI, color: C.ink60, marginTop: 10 }}>
            {Math.round(data.weeklyHMLD).toLocaleString()}m of {Math.round(data.weeklyHMLDTarget).toLocaleString()}m HMLD
            {data.weekPct < 80 ? ' — plenty of room left.' : data.weekPct <= 100 ? ' — right on track.' : ' — ahead of target.'}
          </div>
        </div>
      </div>

      {/* ── 6. League strip ── */}
      {ssgSummary && (
        <div style={{ padding: '12px 22px 0' }}>
          <div onClick={() => navigate('/player/me/ssg')} style={{
            display: 'flex', alignItems: 'center', gap: 14, borderRadius: 28, background: C.leagueBg,
            border: `1px solid ${C.leagueBorder}`, padding: '18px 20px', cursor: 'pointer',
          }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: C.orange, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 16px ' + FONT_UI, flexShrink: 0 }}>
              {ssgSummary.rank}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: '700 14px ' + FONT_UI }}>{leagueTitle}</div>
              <div style={{ font: '400 11.5px ' + FONT_UI, color: C.ink60, marginTop: 3 }}>
                {ssgSummary.total_points} pts · squad of {data.squadSize}
              </div>
            </div>
            <ChevronRight size={18} color={C.orangeInk} style={{ flexShrink: 0 }} />
          </div>
        </div>
      )}

    </div>
  );
}

function fmtSleepLocal(mins) {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}
